import React, { useRef, useEffect, useState } from 'react';
import { useThree, extend } from '@react-three/fiber';
import { useEnvironment } from '@react-three/drei';
import { useSpring, animated } from '@react-spring/three';
import * as THREE from 'three';

import MainModel from '/models/separated/7_10_2_main.jsx';
import DownloadModel from '/models/separated/20_09_download-button.jsx';
import VideoModel from '/models/separated/23_09_video-button.jsx';
extend({ MainModel, DownloadModel, VideoModel });

export default function Experience({
    data, 
    modelPosition, 
    stationVisible,
    setVideoSrc, 
    setIsLoading,
    setIsModelLoaded
}) {
    const props = useSpring({ positionX: modelPosition, from: { positionX: 0 } });
    const mainModelRef = useRef();
    const downloadModelRef = useRef();
    const videoModelRef = useRef();
    const { scene, gl } = useThree();
    const [isMainLoaded, setIsMainLoaded] = useState(false);
    const [isDownloadLoaded, setIsDownloadLoaded] = useState(false);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    const envMap = useEnvironment({ files: "./hdrs/studio025.hdr" })

    const skyColor = new THREE.Color(0xE6F3FF);  // Light gray sky color

    useEffect(() => {
        if (envMap) {
            scene.environment = envMap;
            scene.environment.encoding = THREE.sRGBEncoding;
            scene.environment.intensity = 0.5;
        }

        // Set a solid color background
        scene.background = skyColor;

        // Set up fog
        scene.fog = new THREE.Fog(skyColor, 100, 1000);

        gl.setPixelRatio(window.devicePixelRatio);
        gl.setSize(window.innerWidth, window.innerHeight);
        gl.outputEncoding = THREE.sRGBEncoding;
        gl.toneMapping = THREE.ACESFilmicToneMapping;
        gl.toneMappingExposure = 0.9;

        return () => {
          scene.environment = null;
          scene.fog = null;
          scene.background = null;
        };
    }, [scene, envMap, gl]);

    useEffect(() => {
        if (isMainLoaded && isDownloadLoaded && isVideoLoaded) {
            setIsModelLoaded(true);
            setIsLoading(false);
        }
    }, [isMainLoaded, isDownloadLoaded, isVideoLoaded, setIsModelLoaded, setIsLoading]);

    return (
        <>      
            {/* Large floor plane */}
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, 0]}>
                <planeGeometry args={[2000, 2000]} />
                <meshStandardMaterial color={skyColor} />
            </mesh>

            <ambientLight intensity={1.5} />
            <directionalLight
                position={[50, 50, 25]}
                intensity={1.75}
                castShadow
                shadow-mapSize-width={2048}
                shadow-mapSize-height={2048}
            />

            <animated.group position-x={props.positionX}>
                <MainModel 
                    ref={mainModelRef}
                    scale={4}
                    position={[0, 0, 0]}
                    onLoad={() => setIsMainLoaded(true)}
                />
                <DownloadModel 
                    ref={downloadModelRef}
                    scale={4}
                    position={[0, 0, 0]}
                    downloadButtonColor="#0000C9"
                    hoverColor="#ffffff"
                    innerHoverColor="#0000C9"
                    stationVisible={stationVisible}
                    data={data}
                    setIsLoading={setIsLoading}
                    onLoad={() => setIsDownloadLoaded(true)}
                />
                <VideoModel 
                    ref={videoModelRef}
                    scale={4}
                    position={[0, 0, 0]}
                    videoButtonColor="#0000C9"
                    hoverColor="#ffffff"
                    innerHoverColor="#0000C9"
                    stationVisible={stationVisible}
                    data={data}
                    setVideoSrc={setVideoSrc}
                    onLoad={() => setIsVideoLoaded(true)}
                />
            </animated.group>
        </>
    );
}